'use client'

import { Button, Stack, Typography, type SxProps } from '@mui/material'
import { useTranslations } from 'next-intl'
import { appPaths } from '@config'
import { ActionButtonData, SectionWrapper } from '@components/common'
import { GradientBackgroundWrapper } from '@components/entities'
import { CustomStyles, getStyles } from '@utils/styles'
import defaultStyles, { StylesClassNames } from './styles'

export type ContactBannerData = Partial<{
  heading: string
  actionButtonData?: ActionButtonData
  description: string
  label?: string
}>

type ContactBannerProps = ContactBannerData & {
  csx?: CustomStyles<StylesClassNames>
  sx?: SxProps
  showDivider?: 'bottom' | 'top'
}

export function ContactBanner({
  csx,
  heading,
  description,
  sx,
  label,
  showDivider,
}: ContactBannerProps) {
  const styles = getStyles(defaultStyles, csx)
  const t = useTranslations('ContactBanner')

  const defaultData: ContactBannerData = {
    label: t('label'),
    heading: t('heading'),
    description: t('description'),
    actionButtonData: {
      children: t('buttonText'),
      variant: 'contained',
    },
  }

  return (
    <SectionWrapper csx={{ wrapper: sx }} showDivider={showDivider}>
      <GradientBackgroundWrapper
        {...styles('wrapper')}
        csx={{
          black: styles('black').sx,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <Typography
            variant="body1.700"
            color="text.primaryDark"
            textAlign="center"
          >
            {label || defaultData.label}
          </Typography>
          <Typography
            variant="h4.700"
            color="text.primaryDark"
            textAlign="center"
          >
            {heading || defaultData.heading}
          </Typography>
          <Typography
            variant="body1.500"
            color="text.primaryDark"
            textAlign="center"
          >
            {description || defaultData.description}
          </Typography>
          <Button
            variant="outlined"
            href={appPaths.contactUs}
            {...styles('button')}
          >
            {defaultData.actionButtonData?.children}
          </Button>
        </Stack>
      </GradientBackgroundWrapper>
    </SectionWrapper>
  )
}
